import { Dialog, SelectV2, SelectV2Option } from '@cimplico/components';
import { WorksheetBase, Worksheet, WorksheetStatusEnum, WorksheetTypeEnum } from '../../../../types/worksheet';
import { Account } from '../../../../types/account';
import { useWorksheets } from '../../../../queries/use-worksheets';
import { useState, useEffect } from 'react';
import { useUpdateWorksheets } from '../../../../queries/use-update-worksheets';
import { toastNotification } from '../../../../components/toast/Toast';
import axios from 'axios';

type WorksheetLinkPriorDialogProps = {
    worksheet: WorksheetBase & { type: WorksheetTypeEnum; account?: Account | null };
    priorWorkpaperId: string;
    onClose: () => void;
};

function WorksheetLinkPriorDialog({ worksheet, priorWorkpaperId, onClose }: WorksheetLinkPriorDialogProps): JSX.Element {
    const [selected, setSelected] = useState<SelectV2Option[]>([]);
    const [search, setSearch] = useState('');

    const priorWorksheetsQuery = useWorksheets(
        { page: 1, limit: 1000, filterQuery: '', searchQuery: '', sortQuery: '', hasAccount: !!worksheet.account },
        priorWorkpaperId
    );
    const currentWorksheetsQuery = useWorksheets({
        page: 1,
        limit: 1000,
        filterQuery: '',
        searchQuery: '',
        sortQuery: '',
        hasAccount: !!worksheet.account,
    });

    const priorWorksheets = priorWorksheetsQuery.data?.worksheets ?? [];
    const currentWorksheets = currentWorksheetsQuery.data?.worksheets ?? [];

    const isLoading = priorWorksheetsQuery.isLoading || currentWorksheetsQuery.isLoading;
    const isError =
        priorWorksheetsQuery.isError ||
        !priorWorksheetsQuery.isSuccess ||
        currentWorksheetsQuery.isError ||
        !currentWorksheetsQuery.isSuccess;

    const updateWorksheetsQuery = useUpdateWorksheets();
    const worksheetType = worksheet.account ? 'worksheet' : 'schedule';

    useEffect(() => {
        if (!isLoading && isError) {
            onClose();
            toastNotification('error', 'Something went wrong, please try again. Contact us if issue persists.');
        }
    }, [isError, isLoading, onClose]);

    function handleWorksheetChange(selectedOption: SelectV2Option[]): void {
        setSelected(selectedOption);
    }

    async function handleLinkPriorWorksheet(): Promise<void> {
        try {
            const priorWorksheetId = selected[0].value.toString();
            await updateWorksheetsQuery.mutateAsync([
                {
                    id: worksheet.id,
                    fields: { priorWorksheetId },
                },
            ]);
            toastNotification('success', `Successfully linked worksheet to ${selected[0].label}`);
            onClose();
        } catch (error) {
            toastNotification(
                'error',
                axios.isAxiosError(error)
                    ? error.response?.data?.results[0]?.error?.fields?.errorMessage
                    : 'Error occured while linking prior worksheet'
            );
        }
    }

    const options = (priorWorksheets as Worksheet[]).map((pw) => {
        return {
            label: pw.name,
            value: pw.id,
            isDisabled: currentWorksheets.some((cw) => cw.priorWorksheetId === pw.id),
            disabledHint: `Already linked to a ${worksheetType} in this workpaper`,
        };
    });

    if (isLoading) {
        return <></>;
    }

    return (
        <Dialog
            title={`Link to Prior ${worksheetType.charAt(0).toUpperCase() + worksheetType.slice(1)}`}
            primaryAction="Create Link"
            secondaryAction="Cancel"
            description={`Creating a link to a prior ${worksheetType} will allow you to carry forward the resources it contains`}
            isOpen
            type="info"
            onClose={onClose}
            onSecondaryActionClick={onClose}
            onPrimaryActionClick={handleLinkPriorWorksheet}
            disablePrimaryAction={!selected.length}
            disabledPrimaryActionHint={'Select a prior worksheet'}
            isLoading={isLoading}
        >
            <div className="pr-6 pb-6 pt-2">
                <SelectV2
                    placeholder={`Select prior ${worksheetType}...`}
                    value={selected}
                    keepSelectedOptions
                    onChange={handleWorksheetChange}
                    onSearch={setSearch}
                    options={options.filter((pw) => pw.label.toLowerCase().includes(search.toLowerCase()))}
                    disabled={options.every((pw) => pw.isDisabled) || !options.length}
                    disabledHint={
                        !options.length
                            ? `Cannot find any ${worksheetType}s in the prior workpaper`
                            : options.every((pw) => pw.isDisabled)
                            ? `Every ${worksheetType} in the prior workpaper has already been linked`
                            : undefined
                    }
                />
            </div>
        </Dialog>
    );
}

export default WorksheetLinkPriorDialog;
